import * as React from 'react'

type Shop = {
  id: string
  _id: string
  name: Record<string, string>
  description?: Record<string, string>
  logo_file: {
    url: string
  }
  ui_language?: 'tr' | 'en'
  plan: string
  slug: string
  settings: {
    languages?: string[]
    [key: string]: any
  }
  organization_id: string
}

interface ShopContextType {
  activeShop: Shop | null
  setActiveShop: (shop: Shop | null) => void
}

const ShopContext = React.createContext<ShopContextType | undefined>(undefined)

export function ShopProvider({ children }: { children: React.ReactNode }) {
  const [activeShop, setActiveShop] = React.useState<Shop | null>(() => {
    try {
      const stored = localStorage.getItem('lastSelectedShop')
      return stored ? JSON.parse(stored) : null
    } catch (error) {
      console.error('Failed to parse lastSelectedShop', error)
      return null
    }
  })

  React.useEffect(() => {
    const handler = () => setActiveShop(null)
    window.addEventListener('clear-active-shop', handler)
    return () => window.removeEventListener('clear-active-shop', handler)
  }, [])

  const value = React.useMemo(
    () => ({
      activeShop,
      setActiveShop,
    }),
    [activeShop]
  )

  return <ShopContext.Provider value={value}>{children}</ShopContext.Provider>
}

export function clearActiveShop() {
  localStorage.removeItem('lastSelectedShop')
  window.dispatchEvent(new Event('clear-active-shop'))
}

export function useShop() {
  const context = React.useContext(ShopContext)
  if (context === undefined) {
    throw new Error('useShop must be used within a ShopProvider')
  }
  return context
}
